import React, { useEffect, useRef, useState } from 'react';
import { MessageList } from 'react-chat-elements';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { getAllMessagesAPI, markReadChatMessagesAPI } from '../../../utils/apiServices/chatsAPI';
import { initializeMessages, prependMessages } from '../../../redux';
import { popup } from '../../../utils';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { CURRENT_VIEW, formattedMessageDate, MESSAGE_ADD_TYPE } from './constant';
import { useDebounce } from '../../../hooks';
import { ChatSearch } from './ChatSearch';

export const ChatMessageBody = ({ messageSearch }) => {
    const dispatch = useDispatch();
    const selectedUser = useSelector((s) => s.chat.selectedUser);
    const messages = useSelector((s) => s.chat.messages);
    const show = useSelector((s) => s.chat.isChatOpen);
    const currentView = useSelector((s) => s.chat.currentView);
    const msgAddType = useSelector((s) => s.chat.messageAddType);
    const isCsmCaseload = useSelector((s) => s.csmCaseload?.isCsmCaseload);
    const scrollRef = useRef(null);

    const [isFetching, setIsFetching] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [search, setSearch] = useState('');

    const getAllMessages = useDebounce(async ({ before, last_message_id, prepend = false }) => {
        if (!selectedUser?.chat_id || isFetching || (prepend && !hasMore)) return;
        setIsFetching(true);
        try {
            const { data, count } = await getAllMessagesAPI({
                chat_id: selectedUser?.chat_id,
                before,
                last_message_id,
                search,
            });
            if (prepend && messages?.length > 0) {
                dispatch(prependMessages(data));
            } else {
                dispatch(initializeMessages(data));
            }

            if (messages?.length + data?.length >= count || data.length === 0) {
                setHasMore(false);
            }
        } catch (error) {
            popup('error', error.message || 'Failed to fetch messages');
        } finally {
            setIsFetching(false);
        }
    }, 500);

    useEffect(() => {
        if (selectedUser?.chat_id && show) {
            getAllMessages({ prepend: false });
        }

        return () => {
            dispatch(initializeMessages([]));
            setHasMore(true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser, messageSearch, show, search]);

    useEffect(() => {
        if (
            currentView === CURRENT_VIEW.MESSAGE_VIEW &&
            scrollRef.current &&
            msgAddType === MESSAGE_ADD_TYPE.APPEND
        ) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                // behavior: 'smooth',
            });
        }
    }, [currentView, msgAddType, messages.length]);

    const handleScroll = async () => {
        const el = scrollRef.current;
        if (!el || el.scrollTop > 50 || isFetching || !hasMore || messages.length < 50) return;

        const firstMsg = messages[0];
        if (!firstMsg) return;

        // const before = firstMsg.date;
        const before = moment(firstMsg.date).format('YYYY-MM-DD HH:mm:ss');
        const last_message_id = firstMsg.id;
        const prevScrollHeight = el.scrollHeight;

        await getAllMessages({ before, prepend: true, last_message_id });

        requestAnimationFrame(() => {
            const newScrollHeight = el.scrollHeight;
            el.scrollTop = newScrollHeight - prevScrollHeight;
        });
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages?.length, isFetching, hasMore]);

    const markMessagesAsRead = async () => {
        await markReadChatMessagesAPI({
            chat_id: selectedUser?.chat_id,
        });
    };

    useEffect(() => {
        return () => {
            if (selectedUser?.chat_id && !isCsmCaseload) {
                markMessagesAsRead();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser?.chat_id]);

    return (
        <>
            <ChatSearch search={search} setSearch={setSearch} placeholder='Search Message...' />
            <div className='chat-body message-body' ref={scrollRef}>
                {isFetching && messages?.length > 0 && (
                    <span className='d-flex justify-content-center'>
                        <Spinner />
                    </span>
                )}
                <MessageList
                    className='message-list'
                    lockable={true}
                    toBottomHeight='100%'
                    dataSource={messages.map((msg) => ({
                        ...msg,
                        title: msg.position === 'right' ? 'You' : msg.title,
                        dateString: formattedMessageDate(msg.date),
                    }))}
                />
            </div>
        </>
    );
};
